import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { currentUserShowSuccess } from '../../ducks/user.duck';
import { createSearchDataApi, createUserInPipedriveApi, fetchBusinessCategoriesApi, updateSearchDataApi, updateUserInPipedriveApi } from '../../util/api';
import { queryUserListings } from '../ProfilePage/ProfilePage.duck';
import { buildPipedrivePayload } from '../../util/dataExtractor';

// ================ Action types ================ //

export const CLEAR_UPDATED_FORM = 'app/ProfileSettingsPage/CLEAR_UPDATED_FORM';

export const UPLOAD_IMAGE_REQUEST = 'app/ProfileSettingsPage/UPLOAD_IMAGE_REQUEST';
export const UPLOAD_IMAGE_SUCCESS = 'app/ProfileSettingsPage/UPLOAD_IMAGE_SUCCESS';
export const UPLOAD_IMAGE_ERROR = 'app/ProfileSettingsPage/UPLOAD_IMAGE_ERROR';

export const UPDATE_PROFILE_REQUEST = 'app/ProfileSettingsPage/UPDATE_PROFILE_REQUEST';
export const UPDATE_PROFILE_SUCCESS = 'app/ProfileSettingsPage/UPDATE_PROFILE_SUCCESS';
export const UPDATE_PROFILE_ERROR = 'app/ProfileSettingsPage/UPDATE_PROFILE_ERROR';

export const FETCH_BUSINESS_CATEGORIES_REQUEST = 'app/ProfileSettingsPage/FETCH_BUSINESS_CATEGORIES_REQUEST';
export const FETCH_BUSINESS_CATEGORIES_SUCCESS = 'app/ProfileSettingsPage/FETCH_BUSINESS_CATEGORIES_SUCCESS';
export const FETCH_BUSINESS_CATEGORIES_ERROR = 'app/ProfileSettingsPage/FETCH_BUSINESS_CATEGORIES_ERROR';

// ================ Reducer ================ //

const initialState = {
  image: null,
  uploadImageError: null,
  uploadInProgress: false,
  updateInProgress: false,
  updateProfileError: null,
  businessCategories: null,
  businessSubcategories: null,
  businessChildSubcategories: null,
  fetchBusinessCategoriesInProgress: false,
  fetchBusinessCategoriesError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case UPLOAD_IMAGE_REQUEST:
      // payload.params: { id: 'tempId', file }
      return {
        ...state,
        image: { ...payload.params },
        uploadInProgress: true,
        uploadImageError: null,
      };
    case UPLOAD_IMAGE_SUCCESS: {
      // payload: { id: 'tempId', uploadedImage }
      const { id, uploadedImage } = payload;
      const { file } = state.image || {};
      const image = { id, imageId: uploadedImage.id, file, uploadedImage };
      return { ...state, image, uploadInProgress: false };
    }
    case UPLOAD_IMAGE_ERROR: {
      // eslint-disable-next-line no-console
      return { ...state, image: null, uploadInProgress: false, uploadImageError: payload.error };
    }

    case UPDATE_PROFILE_REQUEST:
      return {
        ...state,
        updateInProgress: true,
        updateProfileError: null,
      };
    case UPDATE_PROFILE_SUCCESS:
      return {
        ...state,
        image: null,
        updateInProgress: false,
      };
    case UPDATE_PROFILE_ERROR:
      return {
        ...state,
        image: null,
        updateInProgress: false,
        updateProfileError: payload,
      };

    case CLEAR_UPDATED_FORM:
      return { ...state, updateProfileError: null, uploadImageError: null };

    case FETCH_BUSINESS_CATEGORIES_REQUEST:
      return {
        ...state,
        fetchBusinessCategoriesInProgress: true,
        fetchBusinessCategoriesError: null,
      };
    case FETCH_BUSINESS_CATEGORIES_SUCCESS: {
      const { businessCategories, businessSubCategories, businessChildSubCategories
      } = payload;
      return {
        ...state,
        businessCategories,
        businessSubcategories: businessSubCategories,
        businessChildSubcategories: businessChildSubCategories,
        fetchBusinessCategoriesInProgress: false,
      };
    }
    case FETCH_BUSINESS_CATEGORIES_ERROR:
      return {
        ...state,
        fetchBusinessCategoriesInProgress: false,
        fetchBusinessCategoriesError: payload,
      };

    default:
      return state;
  }
}

// ================ Selectors ================ //

// ================ Action creators ================ //

export const clearUpdatedForm = () => ({
  type: CLEAR_UPDATED_FORM,
});

// SDK method: images.upload
export const uploadImageRequest = params => ({ type: UPLOAD_IMAGE_REQUEST, payload: { params } });
export const uploadImageSuccess = result => ({ type: UPLOAD_IMAGE_SUCCESS, payload: result.data });
export const uploadImageError = error => ({
  type: UPLOAD_IMAGE_ERROR,
  payload: error,
  error: true,
});

// SDK method: sdk.currentUser.updateProfile
export const updateProfileRequest = params => ({
  type: UPDATE_PROFILE_REQUEST,
  payload: { params },
});
export const updateProfileSuccess = result => ({
  type: UPDATE_PROFILE_SUCCESS,
  payload: result.data,
});
export const updateProfileError = error => ({
  type: UPDATE_PROFILE_ERROR,
  payload: error,
  error: true,
});

export const fetchBusinessCategoriesRequest = () => ({
  type: FETCH_BUSINESS_CATEGORIES_REQUEST,
});

export const fetchBusinessCategoriesSuccess = response => ({
  type: FETCH_BUSINESS_CATEGORIES_SUCCESS,
  payload: response.data,
});

export const fetchBusinessCategoriesError = error => ({
  type: FETCH_BUSINESS_CATEGORIES_ERROR,
  payload: error,
  error: true,
});

// ================ Thunk ================ //


// Images return imageId which we need to map with previously generated temporary id
export function uploadImage(actionPayload) {
  return (dispatch, getState, sdk) => {
    const id = actionPayload.id;
    dispatch(uploadImageRequest(actionPayload));

    const bodyParams = {
      image: actionPayload.file,
    };
    const queryParams = {
      expand: true,
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    return sdk.images
      .upload(bodyParams, queryParams)
      .then(resp => {
        const uploadedImage = resp.data.data;
        dispatch(uploadImageSuccess({ data: { id, uploadedImage } }));
      })
      .catch(e => dispatch(uploadImageError({ id, error: storableError(e) })));
  };
};

export const createProfileListing = (actionPayload, currentUser) => (dispatch, getState, sdk) => {
  const { firstName, lastName, displayName, publicData = {} } = actionPayload || {};
  const { id, profileImage, attributes } = currentUser || {};

  const {
    company_name,
    business_description,
    operational_states = [],
    businessSubChildCategory,
    businessSubCategory,
    businessCategory = [],
  } = publicData;
  const { userProfileListingId, highlightedProfile = 0 } = attributes?.profile?.publicData || {};

  const listingType = process.env.REACT_APP_TYPE;
  const indexName = process.env.REACT_APP_ALGOLIA_CONTRACTOR_INDEX;

  const buildListing = (listingId, state, createdAt) => ({
    firstName,
    lastName,
    title: company_name,
    description: business_description,
    states: operational_states.map(e => e.label),
    businessCategories: businessCategory.map(e => e.label),
    businessSubCategory,
    businessSubChildCategory,
    objectID: listingId?.uuid,
    state,
    createdAt,
    listingType,
    profileListing: true,
    highlightedProfile: (highlightedProfile == 1) ?  1 : 0,
    authorData: {
      abbreviatedName: attributes?.profile?.abbreviatedName,
      displayName: displayName,
      profileImage,
      authorId: id?.uuid,
    }
  });

  if (userProfileListingId) {
    return sdk.ownListings
      .update({
        id: userProfileListingId,
        title: company_name,
        description: business_description,
        publicData: {
          ...publicData,
          displayName,
          firstName,
          lastName,
          profileListing: true,
          categories: businessCategory.map(e => e.option),
          states: operational_states.map(e => e.key || e.option),
          authorId: id?.uuid,
          highlightedProfile: (highlightedProfile == 1) ?  1 : 0,
        },
      }, { expand: true })
      .then((updateRes) => {
        const { state, createdAt } = updateRes?.data?.data?.attributes || {};
        const listing = buildListing(updateRes?.data?.data?.id, state, createdAt);
        return updateSearchDataApi({ indexName, data: [listing] });
      })
      .catch((error) => {
        console.error('Error updating existing listing:', error);
        throw error;
      });
  }

  return sdk.ownListings
    .create({
      title: company_name,
      description: business_description,
      publicData: {
        ...publicData,
        displayName,
        firstName,
        lastName,
        profileListing: true,
        categories: businessCategory.map(e => e.option),
        states: operational_states.map(e => e.key || e.option),
        authorId: id?.uuid,
        highlightedProfile: (highlightedProfile == 1) ?  1 : 0,
      },
    }, { expand: true })
    .then((createRes) => {
      const { id: listingId, attributes } = createRes?.data?.data || {};
      const { state, createdAt } = attributes || {};
      const listing = buildListing(listingId, state, createdAt);

      return createSearchDataApi({ indexName, data: [listing] }).then((algoliaRes) => {
        if (algoliaRes) {
          return sdk.currentUser
            .updateProfile({ publicData: { userProfileListingId: listingId?.uuid } })
            .then(() => {
              return sdk.ownListings.update({
                id: listingId,
                publicData: { algoliaPublished: true },
              });
            });
        }

        return algoliaRes;
      });
    })
    .catch((error) => {
      console.error('Error creating new listing:', error);
      throw error;
    });
};

export const updatedUserProfileImage = (currentUser, config) => (dispatch, getState, sdk) => {
  const { id: userId } = currentUser || {};

  return dispatch(queryUserListings(userId, config, true)).then((response) => {
    const listings = response?.data?.data || [];
    const { profileImage, attributes, id } = currentUser || {};
    const listingIds = listings.map((e) => {
      return {
        objectID: e.id.uuid, authorData: {
          abbreviatedName: attributes?.profile?.abbreviatedName,
          displayName: attributes?.profile?.displayName,
          profileImage,
          authorId: id?.uuid,
        }
      }
    });

    if (listingIds.length > 0 && profileImage) {
      return updateSearchDataApi({ data: listingIds, profileImage, indexName: process.env.REACT_APP_ALGOLIA_LISTING_INDEX, });
    }
  });
};

export const createUserInPipedrive = (currentUser) => (dispatch, getState, sdk) => {
  const { pipeDriveUserId } = currentUser?.attributes?.profile?.publicData || {};
  if (pipeDriveUserId) {
    const updates = buildPipedrivePayload(currentUser);
    return updateUserInPipedriveApi({ updates, pipeDriveUserId })

  } else {
    const payload = buildPipedrivePayload(currentUser);
    return createUserInPipedriveApi({ payload }).then((response) => {
      return sdk.currentUser
        .updateProfile({ publicData: { pipeDriveUserId: response?.id } })
    });
  }
};

export const updateProfile = (actionPayload, config, profilePage = false) => {
  return (dispatch, getState, sdk) => {
    dispatch(updateProfileRequest());

    const queryParams = {
      expand: true,
      include: ['profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    return sdk.currentUser
      .updateProfile(actionPayload, queryParams)
      .then(response => {
        dispatch(updateProfileSuccess(response));

        const entities = denormalisedResponseEntities(response);
        if (entities.length !== 1) {
          throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
        }
        const currentUser = entities[0];

        // Update current user in state.user.currentUser through user.duck.js
        dispatch(currentUserShowSuccess(currentUser));
        if (profilePage) {
          dispatch(createProfileListing(actionPayload, currentUser));
          dispatch(createUserInPipedrive(currentUser))
          if (actionPayload?.profileImageId) {
            return dispatch(updatedUserProfileImage(currentUser, config))
          }
        }
      })
      .catch(e => dispatch(updateProfileError(storableError(e))));
  };
};

export const fetchBusinessCategories = () => {
  return (dispatch, getState, sdk) => {
    dispatch(fetchBusinessCategoriesRequest());

    return fetchBusinessCategoriesApi({})
      .then(response => {
        dispatch(fetchBusinessCategoriesSuccess(response));
      })
      .catch(e => dispatch(fetchBusinessCategoriesError(storableError(e))));
  };
};

// Load data for ProfileSettingsPage
export const loadData = () => {
  return (dispatch, getState, sdk) => {
    // Fetch business categories when the page loads
    return dispatch(fetchBusinessCategories());
  };
};